import { Company } from '@/models/Company';
import IScheduledAccountRecovery from '@/@types/interfaces/IScheduledAccountRecovery';
import { LOGIN } from '@/routerNames';
import { GlobalSearchServiceProvider } from '@/services/api-providers/GlobalSearchServiceProvider';
import { MeetingsServiceProvider } from '@/services/api-providers/MeetingsServiceProvider';
import { SocketServiceProvider } from '@/services/api-providers/SocketServiceProvider';
import axios from 'axios';
import notificationService from '@/services/NotificationService';
import navigationService from '@/services/NavigationService';
import extensionService from '@/services/ExtensionService';
import authService from './AuthService';
import { WorkspaceServiceProvider } from './api-providers/WorkspaceServiceProvider';
import { BrowseServiceProvider } from './api-providers/BrowseServiceProvider';
import { TagsServiceProvider } from './api-providers/TagsServiceProvider';
import { ArticleServiceProvider } from './api-providers/ArticleServiceProvider';
import { CommentServiceProvider } from '@/services/api-providers/CommentServiceProvider';
import { DraftArticleServiceProvider } from './api-providers/DraftArticleServiceProvider';
import { CompanyServiceProvider } from './api-providers/CompanyServiceProvider';
import { AdminServiceProvider } from './api-providers/AdminServiceProvider';
import { GroupsServiceProvider } from '@/services/api-providers/GroupsServiceProvider';
import { SharedArticleServiceProvider } from '@/services/api-providers/SharedArticleServiceProvider';
import {
  LoginResponse,
  LoginTwoFactorResponse,
  TwoFactorVerificationResponse,
  SignupResponse,
} from '@/@types/responses/AuthResponses';
import { Logger } from '@/other/Logger';
import { Error404, Error422, Error403, Error400, ApiError } from '@/@types/Errors';
import {
  IActiveSessionsResponse,
  ILogoutAllSessionsResponse,
  IRevokeSessionResponse,
} from '@/@types/interfaces/IActiveSession';
import { CompanyUserStatus } from '@/@types/enums/CompanyUserStatus';
import * as routerNames from '@/routerNames';
import { ImageServiceProvider } from '@/services/api-providers/ImageServiceProvider';
import { VideoUploadServiceProvider } from '@/services/api-providers/VideoUploadServiceProvider';
import { FileAttachmentServiceProvider } from '@/services/api-providers/FileAttachmentServiceProvider';
import { GetCurrentUserResponse } from '@/@types/responses/UserResponses';
import { CompanyResponse } from '@/@types/responses/CompanyResponses';
import { AnyJson } from '@/@types/AnyJson';
import { ArticleReactionsServiceProvider } from '@/services/api-providers/ArticleReactionsServiceProvider';
import { NotificationsServiceProvider } from '@/services/api-providers/NotificationsServiceProvider';
import { clearLegacyAuthCookies } from '@/services/authCookie';
import { BotConfig } from '@/models/forms/BotConfig';
import { BillingServiceProvider } from './api-providers/BillingServiceProvider';
import { CalendarServiceProvider } from './api-providers/CalendarServiceProvider';
import { RetentionServiceProvider } from './api-providers/RetentionServiceProvider';
import { AiGenerationServiceProvider } from './api-providers/AiGenerationServiceProvider';
import { ConnectorServiceProvider } from './api-providers/ConnectorServiceProvider';
import { deadSessionLoginQuery } from '@/helpers/appContext';

const log = new Logger('ApiService');

// A slow network must not keep the user from logging out.
const LOGOUT_REQUEST_TIMEOUT_MS = 5000;

/**
 * SNAP-2309 — every API request carries the session as the HttpOnly `snaplore_session` cookie and
 * this header. The API accepts the cookie only with the header, so a cross-site page cannot ride it.
 */
export const SESSION_CSRF_HEADER = 'X-Requested-With';
export const SESSION_CSRF_VALUE = 'snaplore';

// Matches on a path or query boundary, so a host that merely starts with the API origin is not the API.
export const isApiUrl = (
  url: string | undefined,
  base: string | undefined = process.env.VUE_APP_SERVER_URL
): boolean => {
  const origin = (base || '').replace(/\/+$/, '');
  if (!origin || typeof url !== 'string') return false;

  return url === origin || url.startsWith(`${origin}/`) || url.startsWith(`${origin}?`);
};

// Covers the bare `axios.post(...)` calls too (login, 2FA, signup), so none of them can forget the
// cookie. Cross-origin, `withCredentials` is also what lets the browser store the Set-Cookie from a login.
axios.interceptors.request.use((config) => {
  if (isApiUrl(config.url)) {
    config.withCredentials = true;
    config.headers[SESSION_CSRF_HEADER] = SESSION_CSRF_VALUE;
  }

  return config;
});

export class ApiService {
  baseUrl = process.env.VUE_APP_SERVER_URL;
  company = new CompanyServiceProvider(this);
  workspace = new WorkspaceServiceProvider(this);
  browse = new BrowseServiceProvider(this);
  article = new ArticleServiceProvider(this);
  comment = new CommentServiceProvider(this);
  sharedArticle = new SharedArticleServiceProvider(this);
  draft = new DraftArticleServiceProvider(this);
  tags = new TagsServiceProvider(this);
  groups = new GroupsServiceProvider(this);
  admin = new AdminServiceProvider(this);
  image = new ImageServiceProvider(this);
  videoUpload = new VideoUploadServiceProvider(this);
  fileAttachment = new FileAttachmentServiceProvider(this);
  articleReactions = new ArticleReactionsServiceProvider(this);
  notifications = new NotificationsServiceProvider(this);
  socket = new SocketServiceProvider(this);
  billing = new BillingServiceProvider(this);
  globalSearch = new GlobalSearchServiceProvider(this);
  meetings = new MeetingsServiceProvider(this);
  retention = new RetentionServiceProvider(this);
  calendar = new CalendarServiceProvider(this);
  aiGeneration = new AiGenerationServiceProvider(this);
  connector = new ConnectorServiceProvider(this);

  /** Clears JavaScript-readable session cookies left by older builds. Runs once at boot. */
  clearLegacySession() {
    clearLegacyAuthCookies();
  }

  /**
   * Credentials and CSRF header for an API request. Session state is server-side: this never holds
   * or returns a token.
   */
  getRequestConfig(): { withCredentials: boolean; headers: Record<string, string> } {
    return {
      withCredentials: true,
      headers: { [SESSION_CSRF_HEADER]: SESSION_CSRF_VALUE },
    };
  }

  redirect(url: string): void {
    document.location.href = this.baseUrl + '/' + url;
  }

  /** SNAP-2349 / SNAP-2309: a single-use code for a navigation or another origin. Never a token. */
  createAuthHandoff(companyId?: string) {
    return axios.post<{ code: string; expiresAt: string }>(
      this.baseUrl + '/auth/handoff',
      companyId ? { companyId } : {},
      this.getRequestConfig()
    );
  }

  /** Sets the session cookie on the API origin. The response carries no token. */
  exchangeAuthHandoffCode(code: string) {
    return axios.post<{ authenticated: boolean }>(
      this.baseUrl + '/auth/handoff/exchange',
      { code },
      this.getRequestConfig()
    );
  }

  login(email: string, password: string) {
    return axios.post<LoginResponse | LoginTwoFactorResponse>(
      this.baseUrl + '/login',
      { email, password },
      this.getRequestConfig()
    );
  }

  // SNAP-2357 / SNAP-2366: the 2FA endpoints identify the login attempt by the `loginToken` that
  // POST /login returns. `userId` is only a fallback for a response without one (the API
  // is retiring it), so it is sent only when there is no loginToken.
  private twoFactorAttempt(userId: string, loginToken?: string) {
    return loginToken ? { loginToken } : { userId };
  }

  sendTwoFactorCode(userId: string, loginToken?: string) {
    return axios.post(this.baseUrl + '/auth/send-2fa-code', this.twoFactorAttempt(userId, loginToken));
  }

  verifyTwoFactorCode(userId: string, code: string, loginToken?: string) {
    return axios.post<TwoFactorVerificationResponse>(this.baseUrl + '/auth/verify-2fa-code', {
      ...this.twoFactorAttempt(userId, loginToken),
      code,
    });
  }

  resendTwoFactorCode(userId: string, loginToken?: string) {
    return axios.post(this.baseUrl + '/auth/resend-2fa-code', this.twoFactorAttempt(userId, loginToken));
  }

  signup(data: any) {
    return axios.post<SignupResponse>(this.baseUrl + '/signup', data);
  }

  signupInvited(key: string, data: any) {
    return axios.post(this.baseUrl + '/invited/' + key, data);
  }

  getInvitationInfo(key: string) {
    return axios.get(this.baseUrl + '/invited/' + key, this.getRequestConfig());
  }

  /**
   * `app: 'guides'` (and only that value) makes the backend append `?app=guides` to the emailed
   * link, which opens in a new tab with an empty sessionStorage and would lose the Guides journey.
   */
  resetPassword(email: string, app?: string) {
    return axios.post(this.baseUrl + '/password-reset', {
      email,
      ...(app === 'guides' ? { app } : {}),
    });
  }

  resetPasswordToken(token: string, password: string) {
    return axios.post(this.baseUrl + '/password-reset-token', {
      token,
      password,
    });
  }

  checkAccountRecovery(token: string) {
    return axios.post<{ valid: boolean; coolingOffHours: number }>(this.baseUrl + '/account-recovery/check', { token });
  }

  /** Read-only: whether the reset link can still be used. Asked on page load, never consumes the link. */
  checkPasswordResetToken(token: string) {
    return axios.post<{ valid: boolean }>(this.baseUrl + '/password-reset-token/check', { token });
  }

  redeemAccountRecovery(token: string, password: string) {
    return axios.post<{ effectiveAt: string; applied: boolean; coolingOffHours: number }>(
      this.baseUrl + '/account-recovery/redeem',
      { token, password }
    );
  }

  cancelAccountRecovery(token: string) {
    return axios.post<{ success: boolean }>(this.baseUrl + '/account-recovery/cancel', { token });
  }

  /** Read-only: whether the cancel link would cancel now. Asked on page load, never consumes the link. */
  checkAccountRecoveryCancel(token: string) {
    return axios.post<{ valid: boolean }>(this.baseUrl + '/account-recovery/cancel/check', { token });
  }

  /** SNAP-2424: the sign-in email change waiting out its cooling-off period, if one is scheduled. */
  getAccountRecoveryStatus() {
    return axios.get<IScheduledAccountRecovery>(this.baseUrl + '/user/account-recovery', this.getRequestConfig());
  }

  /** SNAP-2424: cancels the scheduled change from the member's own account. */
  cancelOwnAccountRecovery() {
    return axios.post<{ success: boolean }>(
      this.baseUrl + '/user/account-recovery/cancel',
      {},
      this.getRequestConfig()
    );
  }

  getUser() {
    return axios.get<GetCurrentUserResponse>(this.baseUrl + '/user/me', this.getRequestConfig());
  }

  updateUser(data: any) {
    return axios.put(this.baseUrl + '/user/me', data, this.getRequestConfig());
  }

  // ACTIVE SESSIONS (SNAP-2389)
  getActiveSessions() {
    return axios.get<IActiveSessionsResponse>(this.baseUrl + '/user/sessions', this.getRequestConfig());
  }

  /** "Log out this session". `id` is the session record's id, not anything token-like. */
  revokeSession(id: string) {
    return axios.delete<IRevokeSessionResponse>(this.baseUrl + '/user/sessions/' + id, this.getRequestConfig());
  }

  /**
   * "Log out everywhere". `includeCurrent` is REQUIRED to be passed explicitly by the caller
   * and defaults to `false` on both sides of the wire: signing the user out of the device
   * they are holding is never the implicit outcome. See docs/domains/sessions.md.
   */
  logoutAllSessions(includeCurrent = false) {
    return axios.post<ILogoutAllSessionsResponse>(
      this.baseUrl + '/user/sessions/logout-all',
      { includeCurrent },
      this.getRequestConfig()
    );
  }

  /**
   * Revokes a session server side (SNAP-2298). `current` ends only this device's session.
   * Plain axios on purpose: a failure here must not trigger the global 401 handling, which
   * would itself log out.
   */
  logoutUser(scope: 'current' | 'all' = 'current') {
    return axios.post(
      this.baseUrl + '/user/logout',
      { scope },
      { ...this.getRequestConfig(), timeout: LOGOUT_REQUEST_TIMEOUT_MS }
    );
  }

  // COMPANY
  getCompanyById(id: string) {
    return axios.get(this.baseUrl + '/company/' + id + '/info', this.getRequestConfig());
  }

  updateCompany(data: Partial<Company>) {
    return axios.put(this.baseUrl + '/company', data, this.getRequestConfig());
  }

  getCompanyInvitedUsers() {
    return axios.get<{ items: AnyJson[] }>(this.baseUrl + '/company/users/invited', this.getRequestConfig());
  }

  changeCompanyRole(userId: any, role: any) {
    return axios.put(
      this.baseUrl + '/company/user/' + userId + '/role',
      {
        role,
      },
      this.getRequestConfig()
    );
  }

  removeCompanyUser(id: any, data?: { transferToUserId?: string; deleteAll: boolean }) {
    return axios.delete(this.baseUrl + '/company/user/' + id, {
      ...this.getRequestConfig(),
      data,
    });
  }

  getSharedArticlesCount(userId: string) {
    return axios.get(this.baseUrl + '/company/user/' + userId + '/shared-articles-count', this.getRequestConfig());
  }

  changeUserStatus(userId: string, status: CompanyUserStatus) {
    return axios.put(
      this.baseUrl + '/company/user/' + userId + '/status',
      {
        status,
      },
      this.getRequestConfig()
    );
  }

  removeInvitation(id: any) {
    return axios.delete(this.baseUrl + '/company/invite/' + id, this.getRequestConfig());
  }

  // ARTICLES
  getArticles() {
    return axios.get(this.baseUrl + '/article', this.getRequestConfig());
  }

  deleteArticle(id: any) {
    return axios.delete(this.baseUrl + '/article/' + id, this.getRequestConfig());
  }

  handleArchiveArticle(articleIds: any, isArchived: boolean) {
    return axios.put(this.baseUrl + '/article/archived', { articleIds, isArchived }, this.getRequestConfig());
  }

  // WORKSPACE
  //
  // `?productKey=legacy` scopes this to the original pre-products workspaces
  // (productKey null/missing). Without it the response would also include
  // any new how-to / future-product workspaces created from the products
  // portal, polluting the legacy app's sidebar. The reserved value
  // `legacy` is interpreted server-side as "match null/missing".
  getWorkspaces(withUsers = false) {
    const params = new URLSearchParams({ productKey: 'legacy' });
    if (withUsers) params.set('withUsers', '1');
    return axios.get(`${this.baseUrl}/workspace?${params.toString()}`, this.getRequestConfig());
  }

  searchUsers(q: string, invited = false) {
    return axios.get(
      this.baseUrl + '/user/search/' + encodeURIComponent(q) + (invited ? '?invited=1' : ''),
      this.getRequestConfig()
    );
  }

  getDomainCompany(domain: string) {
    return axios.get<{ company: CompanyResponse }>(
      this.baseUrl + '/domain/' + encodeURIComponent(domain),
      this.getRequestConfig()
    );
  }

  // Bot endpoints
  createBotConfig(config: BotConfig) {
    return axios.post(this.baseUrl + '/bot/config', { config }, this.getRequestConfig());
  }

  getBotConfig(userId: string, eventId?: string) {
    return axios.get<{ config: BotConfig | undefined }>(
      this.baseUrl + '/bot/config/' + userId + '/' + eventId,
      this.getRequestConfig()
    );
  }

  // helper function
  handleResponseError(res: any) {
    let message = 'Connection error';

    if (res.response) {
      if (res.response.data.company) {
        return navigationService.navigate({ name: routerNames.INACTIVE_COMPANY });
      }
      if (res.response.status === 401) {
        authService.logout();
        extensionService.logOutUser();
        const fullPath = window.location.hash.slice(1) || '/';
        return navigationService.navigate({ name: LOGIN, query: deadSessionLoginQuery(fullPath) });
      }

      if (res.response.data && res.response.data.error) {
        message = res.response.data.error;
      } else if (res.response.data && res.response.data.errors) {
        const keys = Object.keys(res.response.data.errors);
        message = res.response.data.errors[keys[0]];
      } else {
        message = 'Request error: #' + res.response.status + ' ' + res.response.statusText;
      }

      this.errorStatusHandler(res.response.status, res.response.data, message);
    } else if (res.message) {
      message = res.message;
      log.error(message, { isNetworkError: true });
    } else {
      log.error(res, { isNetworkError: true });
    }
    notificationService.error(message);
  }

  errorStatusHandler(status: number, data: any, message: string) {
    let error;

    switch (status) {
      case 400:
        error = new Error400(message);
        break;
      case 403:
        error = new Error403(message);
        if (data.userInactiveInCompany) {
          if (data.user.companyIds.length) {
            authService.logout();
            extensionService.logOutUser();
            const fullPath = window.location.hash.slice(1) || '/';
            return navigationService.navigate({ name: LOGIN, query: deadSessionLoginQuery(fullPath) });
          }
          return navigationService.navigate({ name: routerNames.USER_DEACTIVATED });
        }
        break;
      case 404:
        error = new Error404(message);
        break;
      case 422:
        error = new Error422(message);
        break;
      default:
        error = new ApiError(message);
    }
    if (error) {
      log.error(error, { isNetworkError: true });
    }
  }
}

const apiService = new ApiService();
export default apiService;
